import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AccessoriesGuard } from '@rci/self-serve-fido/accessories/accessories.guard';
import {
  ACCESSORIES_ROUTE,
  CANCEL_PLAN_ROUTE,
  DATA_MANAGEMENT_ROUTE,
  MAINTENANCE_ROUTE,
  OVERVIEW_ROUTE,
  PTP_URL,
  REPORT_PAYMENT_URL,
  VIEW_BILL_URL,
  WIRELESS_ROUTE,
} from '@rci/self-serve-fido/app.constant';
import { NotFoundComponent } from '@rci/self-serve-fido/core/not-found/not-found.component';
import { SigninGuard } from '@rci/self-serve-fido/core/signin/signin.guard';
import { EnsureNoGlobalApiErrorGuard } from '@rci/self-serve-fido/shared/error/ensure-no-global-api-error.guard';
import { commonGuards, getWirelessDeeplinkGuard } from '@rci/self-serve-fido/guard/routing.guards';
import { selectAccountGuard } from '@rci/self-serve-fido/shared/account-selector-modal/select-account.guard';
import { changeCallerIdAppRoutes } from '@rci/self-serve-fido/change-caller-id/change-caller-id-app-routes';
import { changeVoicemailPasswordAppRoutes } from './change-voicemail/change-voicemail-password-app-routes';
import { rogersBankAppRoutes } from '@rci/self-serve-fido/rogers-bank/rogers-bank-routes';
import { manageSimAppRoutes } from '@rci/self-serve-fido/manage-sim/manage-sim-app-routes';
import { portInAppRoutes } from '@rci/self-serve-fido/port-in/port-in-routes';
import { temporarySuspensionAppRoutes } from '@rci/self-serve-fido/temporary-suspension/temporary-suspension-app-routes';
import { deviceDetailRoute } from '@rci/self-serve-fido/wireless/device/device.routes';
import { managePlanAppRoute } from './wireless/plan/manage/manage-plan-app-route';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: OVERVIEW_ROUTE,
  },
  {
    path: OVERVIEW_ROUTE,
    loadChildren: () => import('./overview/overview.module').then(m => m.OverviewModule),
    canActivate: commonGuards,
  },
  {
    path: WIRELESS_ROUTE,
    loadChildren: () => import('./wireless/wireless.module').then(m => m.WirelessModule),
    canActivate: getWirelessDeeplinkGuard(WIRELESS_ROUTE),
  },
  {
    path: `${WIRELESS_ROUTE}/travel-pass`,
    loadChildren: () => import('./wireless/travel-pass/travel-pass.module').then(m => m.TravelPassModule),
  },
  deviceDetailRoute,
  {
    path: 'change-phone-number',
    loadChildren: () => import('./change-phone-number/change-phone-number.module').then(m => m.ChangePhoneNumberModule),
    canActivate: getWirelessDeeplinkGuard('change-phone-number'),
  },
  {
    path: 'profile-settings',
    loadChildren: () => import('./profile-settings/profile-settings.module').then(m => m.ProfileSettingsModule),
    canActivate: commonGuards,
  },
  ...changeCallerIdAppRoutes,
  ...changeVoicemailPasswordAppRoutes,
  ...manageSimAppRoutes,
  ...portInAppRoutes,
  ...temporarySuspensionAppRoutes,
  managePlanAppRoute,
  {
    path: 'link-account',
    loadChildren: () => import('./link-account/link-account.module').then(m => m.LinkAccountAppModule),
    canActivate: [SigninGuard, EnsureNoGlobalApiErrorGuard],
  },
  {
    path: 'retrieve-puk',
    loadChildren: () => import('./wireless/puk-code/puk-code.module').then(m => m.PukCodeModule),
    canActivate: getWirelessDeeplinkGuard('retrieve-puk'),
  },
  {
    path: 'orders',
    loadChildren: () => import('./my-orders/my-orders-app.module').then(m => m.MyOrdersAppModule),
    canActivate: [...commonGuards, selectAccountGuard],
  },
  {
    path: DATA_MANAGEMENT_ROUTE,
    loadChildren: () => import('./wireless/data-management/data-management.module').then(m => m.DataManagementModule),
    canActivate: commonGuards,
  },
  {
    path: PTP_URL,
    loadChildren: () => import('./promise-to-pay/promise-to-pay.module').then(m => m.PromiseToPayModule),
    canActivate: commonGuards,
  },
  {
    path: REPORT_PAYMENT_URL,
    loadChildren: () => import('./report-payment/report-payment.module').then(m => m.ReportPaymentModule),
    canActivate: commonGuards,
  },
  {
    path: ACCESSORIES_ROUTE,
    loadChildren: () => import('./accessories/accessories.module').then(m => m.AccessoriesModule),
    canActivate: [...commonGuards, AccessoriesGuard],
  },
  {
    path: 'payments',
    loadChildren: () => import('./payments/payments.module').then(m => m.PaymentsModule),
    canActivate: commonGuards,
  },
  {
    path: 'repair-claim',
    loadChildren: () => import('./repair-claim/repair-claim-app.module').then(m => m.RepairClaimAppModule),
    canActivate: [SigninGuard],
  },
  ...rogersBankAppRoutes,
  {
    path: VIEW_BILL_URL,
    loadChildren: () => import('./view-bill/view-bill.module').then(m => m.ViewBillModule),
    canActivate: commonGuards,
  },
  {
    path: MAINTENANCE_ROUTE,
    loadChildren: () => import('./maintenance/maintenance.module').then(m => m.MaintenanceModule),
  },
  {
    path: CANCEL_PLAN_ROUTE,
    loadChildren: () => import('./wireless/plan/cancel/cancel-plan-app-routes').then(m => m.cancelPlanAppRoutes),
  },
  { path: '**', component: NotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
